import { PayloadAction } from '@reduxjs/toolkit';

import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';
import { u21CreateAsyncThunk } from 'app/shared/thunk/u21CreateAsyncThunk';
import { sendErrorToast } from 'app/shared/toasts/actions';
import { Filter } from 'app/modules/filters/models';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { getValidFilters } from 'app/modules/filters/utils';
import { getLocalStorageJSON } from 'app/shared/utils/localStorage';
import { ALL_DATA_SETTING_FILTER_OPTIONS } from 'app/modules/dataSettings/filters';
import {
  DataSettingsState,
  OverlayState,
} from 'app/modules/dataSettings/types';
import {
  BulkCreateCustomDataSettingsResponse,
  DataSettingsKeyType,
  OrgDataSettingsConfig,
  OrgDataSettingsConfigAddEditResponse,
  OrgDataSettingsConfigResponse,
} from 'app/modules/dataSettings/responses';
import {
  addOrgCustomDataSettingsConfig,
  deleteOrgCustomDataSettingsConfig,
  editDataSetting,
  getDataSettings,
} from 'app/modules/dataSettings/api';
import {
  AddOrgCustomDataSettingsRequestPayload,
  EditDataSettingsConfigRequest,
} from 'app/modules/dataSettings/requests';
import { ERROR_STATE, LOADING_STATE } from 'app/shared/types/utils/asyncStatus';
import { cloneDeep } from 'lodash';
import {
  BASE_DATA_SETTINGS_BY_NATIVE_KEY,
  UI_UNAVAILABLE_DATA_SETTINGS_BY_CLASSIFIER,
} from 'app/modules/dataSettings/constants';

const DATA_SETTINGS_NAME = 'dataSettings';

const NO_OVERLAY_STATE = {
  overlay: 'NONE',
} as const;

const initialState: DataSettingsState = {
  dataSettings: LOADING_STATE,
  filters: getValidFilters(
    getLocalStorageJSON(LocalStorageKeys.DATA_SETTINGS_FILTERS),
    ALL_DATA_SETTING_FILTER_OPTIONS,
  ),
  overlay: NO_OVERLAY_STATE,
  dataSettingsById: {},
  dataSettingsByNativeKey: BASE_DATA_SETTINGS_BY_NATIVE_KEY,
  uiAvailableDataSettings: [],
};

export const getOrgCustomDataSettingsConfigsThunk = u21CreateAsyncThunk<
  void,
  OrgDataSettingsConfigResponse
>(
  `${DATA_SETTINGS_NAME}/GET_ORG_CUSTOM_DATA_SETTINGS_CONFIGS`,
  async (_, { dispatch }) => {
    try {
      return await getDataSettings();
    } catch (e) {
      dispatch(sendErrorToast('Something went wrong'));
      throw e;
    }
  },
);

export const deleteOrgCustomDataSettingsConfigThunk = u21CreateAsyncThunk<
  { id: number },
  Record<string, never>
>(
  `${DATA_SETTINGS_NAME}/DELETE_ORG_CUSTOM_DATA_SETTINGS_CONFIGS`,
  async ({ id }, { dispatch }) => {
    try {
      const retVal = await deleteOrgCustomDataSettingsConfig(id);
      dispatch(getOrgCustomDataSettingsConfigsThunk());
      return retVal;
    } catch (e) {
      dispatch(sendErrorToast('An error ocurred'));
      throw e;
    }
  },
);

export const addOrgCustomDataSettingsConfigThunk = u21CreateAsyncThunk<
  { payload: AddOrgCustomDataSettingsRequestPayload },
  BulkCreateCustomDataSettingsResponse
>(
  `${DATA_SETTINGS_NAME}/ADD_ORG_CUSTOM_DATA_SETTING_CONFIG`,
  async ({ payload }, { dispatch }) => {
    try {
      const retVal = await addOrgCustomDataSettingsConfig({
        items: [payload],
      });
      dispatch(getOrgCustomDataSettingsConfigsThunk());
      return retVal;
    } catch (e) {
      let errorMessage = 'An error ocurred';

      // Handle other API errors with specific messages
      try {
        const errorData = await e.json();
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // If we can't parse JSON, keep the generic message
      }

      dispatch(sendErrorToast(errorMessage));
      throw new Error(errorMessage);
    }
  },
);

export const editOrgCustomDataSettingsConfigThunk = u21CreateAsyncThunk<
  { id: number; payload: EditDataSettingsConfigRequest },
  OrgDataSettingsConfigAddEditResponse
>(
  `${DATA_SETTINGS_NAME}/EDIT_ORG_CUSTOM_DATA_SETTING_CONFIG`,
  async ({ id, payload }, { dispatch }) => {
    try {
      const retVal = await editDataSetting(id, payload);
      dispatch(getOrgCustomDataSettingsConfigsThunk());
      return retVal;
    } catch (e) {
      let errorMessage = 'An error ocurred';

      // Handle API errors with specific messages
      try {
        const errorData = await e.json();
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // If we can't parse JSON, keep the generic message
      }

      dispatch(sendErrorToast(errorMessage));
      throw new Error(errorMessage);
    }
  },
);

export const setDataSettingsPersistentFilters = u21CreateAsyncThunk<
  Filter[],
  Filter[]
>(`${DATA_SETTINGS_NAME}/SET_DATA_SETTINGS_FILTERS`, (payload) => {
  localStorage.setItem(
    LocalStorageKeys.DATA_SETTINGS_FILTERS,
    JSON.stringify(payload),
  );
  return payload;
});

const customDataSlice = u21CreateSlice({
  name: DATA_SETTINGS_NAME,
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getOrgCustomDataSettingsConfigsThunk.pending, (draft) => {
        draft.dataSettings = LOADING_STATE;
      })
      .addCase(getOrgCustomDataSettingsConfigsThunk.rejected, (draft) => {
        draft.dataSettings = ERROR_STATE;
      })
      .addCase(
        getOrgCustomDataSettingsConfigsThunk.fulfilled,
        (draft, { payload }) => {
          draft.dataSettings = {
            status: 'COMPLETE',
            ...payload,
          };

          // Normalize data settings
          const dataSettingsById: Record<string, OrgDataSettingsConfig> = {};
          const dataSettingsByNativeKey = cloneDeep(
            BASE_DATA_SETTINGS_BY_NATIVE_KEY,
          );
          const uiAvailableDataSettings: OrgDataSettingsConfig[] = [];

          payload.data.forEach((dataSetting) => {
            dataSettingsById[dataSetting.id] = dataSetting;
            // all custom data settings are ui available
            if (dataSetting.key_type === DataSettingsKeyType.CUSTOM) {
              uiAvailableDataSettings.push(dataSetting);
            }
            if (dataSetting.key_type === DataSettingsKeyType.NATIVE) {
              // add all native data settings to dataSettingsByNativeKey
              dataSettingsByNativeKey[dataSetting.unit21_data_classifier][
                dataSetting.native_key
              ] = dataSetting;
              // there are some native data settings that are not ui available
              // so we only add data settings to uiAvailableDataSettings
              // if they are not in the UI_UNAVAILABLE_DATA_SETTINGS_BY_CLASSIFIER
              if (
                !UI_UNAVAILABLE_DATA_SETTINGS_BY_CLASSIFIER[
                  dataSetting.unit21_data_classifier
                ]?.has(dataSetting.native_key)
              ) {
                uiAvailableDataSettings.push(dataSetting);
              }
            }
          });
          // add all normalized arrangements to the state
          draft.dataSettingsById = dataSettingsById;
          draft.dataSettingsByNativeKey = dataSettingsByNativeKey;
          draft.uiAvailableDataSettings = uiAvailableDataSettings;
        },
      )
      .addCase(setDataSettingsPersistentFilters.fulfilled, (draft, action) => {
        // store the filters in local storage
        draft.filters = action.payload;
      });
  },
  reducers: {
    setOverlay: (draft, { payload }: PayloadAction<OverlayState>) => {
      draft.overlay = payload;
    },
    clearOverlay: (draft) => {
      draft.overlay = NO_OVERLAY_STATE;
    },
  },
});

export const { name, reducer, actions } = customDataSlice;
export const { setOverlay, clearOverlay } = actions;
